import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Box, Drawer, IconButton, Typography, Divider, Link } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { MC_SURFACE } from './tokens';
import { StatusBadge } from './primitives';
import type { McStatus } from './tokens';
import { ACCENT, TYPE } from '../design-system/foundation';
import { Overline } from '../design-system/v2';

export type InspectorEntityKind =
  | 'mission'
  | 'incident'
  | 'executor'
  | 'evidence'
  | 'validation'
  | 'timeline'
  | 'worker'
  | 'file'
  | 'runbook'
  | 'certification'
  | 'generic';

export interface InspectorPayload {
  kind: InspectorEntityKind;
  title: string;
  subtitle?: string;
  status?: McStatus;
  /** Overrides the default status label (e.g. "Pending" instead of "Unknown") */
  statusLabel?: string;
  fields?: Array<{ label: string; value: string }>;
  body?: string;
  href?: string;
  meta?: Record<string, string>;
}

interface McInspectorValue {
  open: boolean;
  payload: InspectorPayload | null;
  inspect: (payload: InspectorPayload) => void;
  close: () => void;
}

const McInspectorContext = createContext<McInspectorValue | null>(null);

export const McInspectorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<InspectorPayload | null>(null);

  const inspect = useCallback((p: InspectorPayload) => {
    setPayload(p);
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const value = useMemo(() => ({ open, payload, inspect, close }), [open, payload, inspect, close]);

  return (
    <McInspectorContext.Provider value={value}>
      {children}
      <InspectorPanel />
    </McInspectorContext.Provider>
  );
};

export function useMcInspector(): McInspectorValue {
  const ctx = useContext(McInspectorContext);
  if (!ctx) {
    throw new Error('useMcInspector must be used within McInspectorProvider');
  }
  return ctx;
}

/** Safe optional hook when provider may be absent */
export function useMcInspectorOptional(): McInspectorValue | null {
  return useContext(McInspectorContext);
}

const KIND_LABEL: Record<InspectorEntityKind, string> = {
  mission: 'Mission',
  incident: 'Incident',
  executor: 'Executor',
  evidence: 'Evidence',
  validation: 'Validation',
  timeline: 'Timeline',
  worker: 'Worker',
  file: 'File',
  runbook: 'Runbook',
  certification: 'Certification',
  generic: 'Details',
};

const InspectorPanel: React.FC = () => {
  const { open, payload, close } = useMcInspector();
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          bgcolor: MC_SURFACE.panel,
          borderLeft: `1px solid ${MC_SURFACE.border}`,
          backgroundImage: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Overline>{payload ? KIND_LABEL[payload.kind] ?? 'Details' : 'Inspector'}</Overline>
        <IconButton size="small" onClick={close} aria-label="Close inspector">
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <Divider />
      {payload ? (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h2" sx={{ ...TYPE.sectionTitle, color: 'text.primary', m: 0 }}>
                {payload.title}
              </Typography>
              {payload.subtitle ? (
                <Typography sx={{ ...TYPE.caption, color: 'text.secondary', mt: 0.5 }}>{payload.subtitle}</Typography>
              ) : null}
            </Box>
            {payload.status ? <StatusBadge status={payload.status} label={payload.statusLabel} /> : null}
          </Box>
          {payload.body ? (
            <Typography sx={{ ...TYPE.bodySm, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
              {payload.body}
            </Typography>
          ) : null}
          {payload.fields?.length ? (
            <Box component="dl" sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5, mb: 0 }}>
              {payload.fields.map((f) => (
                <Box key={f.label} sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}>
                  <Typography component="dt" sx={{ ...TYPE.label, color: 'text.secondary' }}>
                    {f.label}
                  </Typography>
                  <Typography component="dd" sx={{ ...TYPE.bodySm, color: 'text.primary', wordBreak: 'break-word', m: 0 }}>
                    {f.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}
          {payload.href ? (
            <Link href={payload.href} underline="hover" sx={{ mt: 1, ...TYPE.bodySm, fontWeight: 600, color: ACCENT.ink }}>
              Open full view
            </Link>
          ) : null}
        </Box>
      ) : null}
    </Drawer>
  );
};
